import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { ArrowLeft, RotateCw, ArrowDown, ArrowRight, Pause, Play } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import PixelBackground from '@/components/PixelBackground';
import Seo from '@/components/Seo';
import { BASE_URL, getHreflangLinks, getPageMeta } from '@/seo/meta';
import { stripLanguagePrefix, withLanguagePrefix } from '@/lib/locale';

// Tetris game constants - optimized for mobile
const BOARD_WIDTH = 8; // Reduced from 10 for mobile
const BOARD_HEIGHT = 14; // Further reduced for better fit
const EMPTY_CELL = 0;

// Tetris pieces (tetrominoes)
const PIECES = [
  // I-piece
  [
    [1, 1, 1, 1]
  ],
  // O-piece
  [
    [1, 1],
    [1, 1]
  ],
  // T-piece
  [
    [0, 1, 0],
    [1, 1, 1]
  ],
  // S-piece
  [
    [0, 1, 1],
    [1, 1, 0]
  ],
  // Z-piece
  [
    [1, 1, 0],
    [0, 1, 1]
  ],
  // J-piece
  [
    [1, 0, 0],
    [1, 1, 1]
  ],
  // L-piece
  [
    [0, 0, 1],
    [1, 1, 1]
  ]
];

const PIECE_COLORS = [
  'rgba(240, 171, 252, 0.95)', // I-piece - primary-300
  'rgba(244, 63, 94, 0.95)',   // O-piece - brand highlight
  'rgba(217, 70, 239, 0.95)',  // T-piece - primary-500
  'rgba(232, 121, 249, 0.95)', // S-piece - primary-400
  'rgba(192, 38, 211, 0.95)',  // Z-piece - primary-600
  'rgba(162, 28, 175, 0.95)',  // J-piece - primary-700
  'rgba(192, 132, 252, 0.95)'  // L-piece - accent lavender
];

interface Position {
  x: number;
  y: number;
}

interface Piece {
  shape: number[][];
  position: Position;
  type: number;
}

const TetrisPage: React.FC = () => {
  const { t, language } = useLanguage();
  const location = useLocation();
  const locale = language === 'en' ? 'en' : 'el';
  const meta = getPageMeta('tetris', undefined, locale);
  const canonicalPath = withLanguagePrefix(stripLanguagePrefix(location.pathname), locale);
  const canonicalUrl = `${BASE_URL}${canonicalPath}`;
  const hreflang = getHreflangLinks(location.pathname, locale);
  const [board, setBoard] = useState<number[][]>(() => 
    Array(BOARD_HEIGHT).fill(null).map(() => Array(BOARD_WIDTH).fill(EMPTY_CELL))
  );
  const [currentPiece, setCurrentPiece] = useState<Piece | null>(null);
  const [score, setScore] = useState(0);
  const [level, setLevel] = useState(1);
  const [lines, setLines] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isPlaying, setIsPlaying] = useState(true); // Auto-start
  const gameLoopRef = useRef<NodeJS.Timeout | null>(null);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Create a new random piece
  const createNewPiece = useCallback((): Piece => {
    const pieceType = Math.floor(Math.random() * PIECES.length);
    return {
      shape: PIECES[pieceType],
      position: { x: Math.floor(BOARD_WIDTH / 2) - 1, y: 0 },
      type: pieceType
    };
  }, []);

  // Check if a piece can be placed at a position
  const canPlacePiece = useCallback((piece: Piece, newPosition: Position, newShape?: number[][]): boolean => {
    const shape = newShape || piece.shape;
    
    for (let y = 0; y < shape.length; y++) {
      for (let x = 0; x < shape[y].length; x++) {
        if (shape[y][x]) {
          const boardX = newPosition.x + x;
          const boardY = newPosition.y + y;
          
          if (boardX < 0 || boardX >= BOARD_WIDTH || boardY >= BOARD_HEIGHT) {
            return false;
          }
          
          if (boardY >= 0 && board[boardY][boardX] !== EMPTY_CELL) {
            return false;
          }
        }
      }
    }
    return true;
  }, [board]);

  // Rotate a piece 90 degrees clockwise
  const rotatePiece = useCallback((shape: number[][]): number[][] => {
    const rows = shape.length;
    const cols = shape[0].length;
    const rotated = Array(cols).fill(null).map(() => Array(rows).fill(0));
    
    for (let y = 0; y < rows; y++) {
      for (let x = 0; x < cols; x++) {
        rotated[x][rows - 1 - y] = shape[y][x];
      }
    }
    
    return rotated;
  }, []);

  // Place piece on board
  const placePieceOnBoard = useCallback((piece: Piece): number[][] => {
    const newBoard = board.map(row => [...row]);
    
    for (let y = 0; y < piece.shape.length; y++) {
      for (let x = 0; x < piece.shape[y].length; x++) {
        if (piece.shape[y][x]) {
          const boardY = piece.position.y + y;
          const boardX = piece.position.x + x;
          if (boardY >= 0) {
            newBoard[boardY][boardX] = piece.type + 1;
          }
        }
      }
    }
    
    return newBoard;
  }, [board]);

  // Clear completed lines
  const clearLines = useCallback((gameBoard: number[][]): { newBoard: number[][]; linesCleared: number } => {
    const newBoard = gameBoard.filter(row => row.some(cell => cell === EMPTY_CELL));
    const linesCleared = BOARD_HEIGHT - newBoard.length;
    
    while (newBoard.length < BOARD_HEIGHT) {
      newBoard.unshift(Array(BOARD_WIDTH).fill(EMPTY_CELL));
    }
    
    return { newBoard, linesCleared };
  }, []);

  // Move piece
  const movePiece = useCallback((direction: 'left' | 'right' | 'down') => {
    if (!currentPiece || gameOver || isPaused) return;

    const newPosition = { ...currentPiece.position };
    
    switch (direction) {
      case 'left':
        newPosition.x -= 1;
        break;
      case 'right':
        newPosition.x += 1;
        break;
      case 'down':
        newPosition.y += 1;
        break;
    }

    if (canPlacePiece(currentPiece, newPosition)) {
      setCurrentPiece({ ...currentPiece, position: newPosition });
    } else if (direction === 'down') {
      // Piece can't move down, place it on the board
      const newBoard = placePieceOnBoard(currentPiece);
      const { newBoard: clearedBoard, linesCleared } = clearLines(newBoard);
      
      setBoard(clearedBoard);
      setLines(prev => prev + linesCleared);
      setScore(prev => prev + linesCleared * 100 * level);
      
      // Create new piece
      const newPiece = createNewPiece();
      if (canPlacePiece(newPiece, newPiece.position)) {
        setCurrentPiece(newPiece);
      } else {
        setGameOver(true);
        setIsPlaying(false);
      }
    }
  }, [currentPiece, gameOver, isPaused, canPlacePiece, placePieceOnBoard, clearLines, level, createNewPiece]);

  // Rotate current piece
  const rotatePieceHandler = useCallback(() => {
    if (!currentPiece || gameOver || isPaused) return;

    const rotatedShape = rotatePiece(currentPiece.shape);
    if (canPlacePiece(currentPiece, currentPiece.position, rotatedShape)) {
      setCurrentPiece({ ...currentPiece, shape: rotatedShape });
    }
  }, [currentPiece, gameOver, isPaused, rotatePiece, canPlacePiece]);

  // Drop piece instantly and lock it in place
  const dropPiece = useCallback(() => {
    if (!currentPiece || gameOver || isPaused) return;

    let newY = currentPiece.position.y;
    while (canPlacePiece(currentPiece, { ...currentPiece.position, y: newY + 1 })) {
      newY++;
    }

    const droppedPiece = { ...currentPiece, position: { ...currentPiece.position, y: newY } };
    const newBoard = placePieceOnBoard(droppedPiece);
    const { newBoard: clearedBoard, linesCleared } = clearLines(newBoard);

    setBoard(clearedBoard);
    setLines(prev => prev + linesCleared);
    setScore(prev => prev + linesCleared * 100 * level);

    const nextPiece = createNewPiece();
    if (canPlacePiece(nextPiece, nextPiece.position)) {
      setCurrentPiece(nextPiece);
    } else {
      setGameOver(true);
      setIsPlaying(false);
    }
  }, [
    currentPiece,
    gameOver,
    isPaused,
    canPlacePiece,
    placePieceOnBoard,
    clearLines,
    level,
    createNewPiece,
  ]);

  // Auto-start game
  useEffect(() => {
    if (!currentPiece && !gameOver) {
      setCurrentPiece(createNewPiece());
    }
  }, [currentPiece, gameOver, createNewPiece]);

  // Game loop
  useEffect(() => {
    if (isPlaying && !isPaused && !gameOver) {
      const speed = Math.max(300, 1200 - (level - 1) * 100); // Slower for mobile
      gameLoopRef.current = setInterval(() => {
        movePiece('down');
      }, speed);
    } else {
      if (gameLoopRef.current) {
        clearInterval(gameLoopRef.current);
        gameLoopRef.current = null;
      }
    }

    return () => {
      if (gameLoopRef.current) {
        clearInterval(gameLoopRef.current);
      }
    };
  }, [isPlaying, isPaused, gameOver, level, movePiece]);

  // Update level based on lines cleared
  useEffect(() => {
    setLevel(Math.floor(lines / 10) + 1);
  }, [lines]);

  // Start new game
  const startNewGame = () => {
    setBoard(Array(BOARD_HEIGHT).fill(null).map(() => Array(BOARD_WIDTH).fill(EMPTY_CELL)));
    setCurrentPiece(createNewPiece());
    setScore(0);
    setLevel(1);
    setLines(0);
    setGameOver(false);
    setIsPaused(false);
    setIsPlaying(true);
  };

  // Render the game board with larger blocks for mobile
  const renderBoard = () => {
    const displayBoard = board.map(row => [...row]);
    
    // Add current piece to display board
    if (currentPiece) {
      for (let y = 0; y < currentPiece.shape.length; y++) {
        for (let x = 0; x < currentPiece.shape[y].length; x++) {
          if (currentPiece.shape[y][x]) {
            const boardY = currentPiece.position.y + y;
            const boardX = currentPiece.position.x + x;
            if (boardY >= 0 && boardY < BOARD_HEIGHT && boardX >= 0 && boardX < BOARD_WIDTH) {
              displayBoard[boardY][boardX] = currentPiece.type + 1;
            }
          }
        }
      }
    }

    return displayBoard.map((row, y) => (
      <div key={y} className="flex">
        {row.map((cell, x) => (
          <div
            key={x}
            className="w-7 h-7 sm:w-8 sm:h-8 border border-white/10 rounded-sm"
            style={{
              backgroundColor: cell === EMPTY_CELL ? 'rgba(11, 10, 14, 0.6)' : PIECE_COLORS[cell - 1],
              borderColor: cell === EMPTY_CELL ? 'rgba(255, 255, 255, 0.08)' : 'rgba(255, 255, 255, 0.35)',
              boxShadow: cell !== EMPTY_CELL ? '0 6px 12px rgba(0,0,0,0.35)' : 'inset 0 0 0 1px rgba(255,255,255,0.02)'
            }}
          />
        ))}
      </div>
    ));
  };

  return (
    <div className="relative h-screen bg-pp-black text-white overflow-hidden">
      <Seo
        title={meta.title}
        description={meta.description}
        url={canonicalUrl}
        hreflang={hreflang}
        image={meta.image}
      />
      <PixelBackground />
      <div className="absolute inset-0 geometric-pattern pointer-events-none" />
      <div className="absolute inset-0 bg-gradient-to-br from-pp-black/95 via-black/85 to-black pointer-events-none" />
      {/* Mobile-first layout */}
      <div className="relative z-10 flex flex-col h-screen">
        {/* Header - Compact for mobile */}
        <div className="flex items-center justify-between px-4 py-3 bg-pp-black/70 backdrop-blur-xl border-b border-white/10 flex-shrink-0">
          <button
            onClick={() => window.navigateToHome?.()}
            className="flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-xs font-medium text-white/90 transition hover:border-brand-highlight/60 hover:bg-white/10 hover:text-white z-50"
          >
            <ArrowLeft className="h-4 w-4 mr-1.5" />
            {t('tetris.back')}
          </button>
          <h1 className="text-lg font-semibold text-white/95 tracking-tight">
            {t('tetris.title')}
          </h1>
          <button
            onClick={() => setIsPaused(!isPaused)}
            className="glassmorphic-button rounded-full p-2 text-white/90 transition-all duration-300 hover:border-brand-highlight/60 hover:text-white disabled:opacity-50 z-50"
            disabled={!isPlaying || gameOver}
          >
            {isPaused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
          </button>
        </div>

        {/* Game Area - Takes most of the screen */}
        <div className="flex-1 flex flex-col items-center justify-center p-1 min-h-0">
          {/* Score Bar - Horizontal on mobile */}
          <div className="w-full max-w-sm bg-pp-black/70 backdrop-blur-xl border border-white/10 rounded-2xl p-3 flex-shrink-0 mb-2 shadow-glass">
            <div className="flex justify-between items-center text-xs">
              <div className="text-center">
                <div className="text-xs text-white/50">{t('tetris.score')}</div>
                <div className="font-semibold text-brand-highlight">{score.toLocaleString()}</div>
              </div>
              <div className="text-center">
                <div className="text-xs text-white/50">{t('tetris.level')}</div>
                <div className="font-semibold text-primary-300">{level}</div>
              </div>
              <div className="text-center">
                <div className="text-xs text-white/50">{t('tetris.lines')}</div>
                <div className="font-semibold text-brand-highlight">{lines}</div>
              </div>
            </div>
          </div>

          {/* Game Board - Centered and larger */}
          <div className="bg-pp-black/70 backdrop-blur-xl border border-white/15 p-2 rounded-2xl shadow-glass flex-1 flex items-center justify-center max-h-[calc(100vh-200px)]">
            <div className="flex flex-col">
              {renderBoard()}
            </div>
          </div>

          {/* Game Status Messages */}
          {gameOver && (
            <div className="absolute inset-0 bg-pp-black/90 backdrop-blur-sm flex items-center justify-center z-20">
              <div className="bg-pp-black/80 backdrop-blur-xl border border-brand-highlight/40 p-5 rounded-2xl text-center max-w-sm w-full mx-4 shadow-glass">
              <h2 className="text-lg font-semibold mb-2 text-brand-highlight">{t('tetris.gameover')}</h2>
              <p className="text-sm mb-4 text-white/70">
                {t('tetris.finalscore')} <span className="text-brand-highlight font-semibold">{score.toLocaleString()}</span>
              </p>
              <button
                onClick={startNewGame}
                className="w-full rounded-full border border-white/10 bg-white py-2.5 px-4 text-sm font-semibold text-black transition-all duration-300 hover:border-brand-highlight/60 hover:bg-brand-highlight hover:text-white"
              >
                {t('tetris.newgame')}
              </button>
              </div>
            </div>
          )}

          {isPaused && isPlaying && !gameOver && (
            <div className="absolute inset-0 bg-pp-black/90 backdrop-blur-sm flex items-center justify-center z-20">
              <div className="bg-pp-black/80 backdrop-blur-xl border border-white/15 p-5 rounded-2xl text-center max-w-sm w-full mx-4 shadow-glass">
                <h2 className="text-lg font-semibold text-brand-highlight">{t('tetris.pause')}</h2>
                <p className="text-xs text-white/60 mt-1 mb-4">Click anywhere to continue</p>
                <button
                  onClick={() => setIsPaused(false)}
                  className="w-full rounded-full border border-white/10 bg-white py-2.5 px-4 text-sm font-semibold text-black transition-all duration-300 hover:border-brand-highlight/60 hover:bg-brand-highlight hover:text-white"
                >
                  {t('tetris.continue')}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Touch Controls - Bottom of screen */}
        <div className="bg-pp-black/70 backdrop-blur-xl border-t border-white/10 p-3 flex-shrink-0">
          <div className="grid grid-cols-4 gap-2 max-w-sm mx-auto">
            <button
              onClick={() => movePiece('left')}
              className="glassmorphic-button rounded-xl p-3 flex items-center justify-center transition-all duration-200 active:scale-95 hover:border-brand-highlight/50 disabled:opacity-50"
              disabled={!isPlaying || isPaused || gameOver}
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <button
              onClick={() => movePiece('down')}
              className="glassmorphic-button rounded-xl p-3 flex items-center justify-center transition-all duration-200 active:scale-95 hover:border-brand-highlight/50 disabled:opacity-50"
              disabled={!isPlaying || isPaused || gameOver}
            >
              <ArrowDown className="h-5 w-5" />
            </button>
            <button
              onClick={() => movePiece('right')}
              className="glassmorphic-button rounded-xl p-3 flex items-center justify-center transition-all duration-200 active:scale-95 hover:border-brand-highlight/50 disabled:opacity-50"
              disabled={!isPlaying || isPaused || gameOver}
            >
              <ArrowRight className="h-5 w-5" />
            </button>
            <button
              onClick={rotatePieceHandler}
              className="rounded-xl p-3 text-xs font-medium transition-all duration-200 shadow-glass text-brand-highlight border border-brand-highlight/40 bg-brand-highlight/10 hover:bg-brand-highlight/20 hover:text-white active:scale-95 disabled:opacity-50"
              disabled={!isPlaying || isPaused || gameOver}
            >
              <RotateCw className="h-5 w-5" />
            </button>
          </div>
          
          {/* Quick Drop Button */}
          <button
            onClick={dropPiece}
            className="w-full mt-2 max-w-sm mx-auto block rounded-full border border-brand-highlight/40 bg-brand-highlight/10 py-2.5 px-4 text-xs font-semibold text-brand-highlight transition-all duration-300 shadow-glass hover:bg-brand-highlight hover:text-white active:scale-95 disabled:opacity-50"
            disabled={!isPlaying || isPaused || gameOver}
          >
            {t('tetris.quickdrop')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TetrisPage;
